/**
 * letpot CrudDataSource (PRIVATE fork) — adapts @greenhouse/crud to letpot-api's
 * /admin path conventions (create is POST /:resource/add, ids are Mongo `_id`).
 * Everything rides dashboardFetch, which injects the active X-Workspace header.
 */

import type { CrudDataSource, ListParams, ListResult } from '@greenhouse/crud';
import { dashboardFetch } from './workspace';

async function unwrap(res: Response): Promise<any> {
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    const msg =
      data && typeof data === 'object' && typeof (data as { error?: unknown }).error === 'string'
        ? (data as { error: string }).error
        : `Request failed (${res.status})`;
    throw new Error(msg);
  }
  const text = await res.text();
  return text ? JSON.parse(text) : null;
}

const JSON_HEADERS = { 'Content-Type': 'application/json' };

export function letpotDataSource<TRow>(resource: string): CrudDataSource<TRow> {
  const base = `/api/dashboard/${resource}`;
  return {
    async list(params: ListParams): Promise<ListResult<TRow>> {
      const data = await unwrap(
        await dashboardFetch(`${base}/list`, {
          method: 'POST',
          headers: JSON_HEADERS,
          body: JSON.stringify(params ?? {}),
        }),
      );
      const items = (data?.items ?? []) as TRow[];
      return { items, total: data?.total ?? items.length };
    },
    async get(id: string): Promise<TRow> {
      return unwrap(await dashboardFetch(`${base}/${encodeURIComponent(id)}`));
    },
    async create(data: Record<string, unknown>): Promise<unknown> {
      // letpot creates via POST /:resource/add (not the REST /:resource).
      return unwrap(
        await dashboardFetch(`${base}/add`, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(data) }),
      );
    },
    async update(id: string, data: Record<string, unknown>): Promise<unknown> {
      return unwrap(
        await dashboardFetch(`${base}/${encodeURIComponent(id)}`, {
          method: 'PUT',
          headers: JSON_HEADERS,
          body: JSON.stringify(data),
        }),
      );
    },
    async remove(id: string): Promise<unknown> {
      return unwrap(await dashboardFetch(`${base}/${encodeURIComponent(id)}`, { method: 'DELETE' }));
    },
  };
}
